// Catalog.ID Client — Trust Given Pane
// ============================================================================
// Lists outgoing trust expressions (ratings the user has given to others), and
// is where giving one starts: rating somebody happens on their public profile,
// so the pane's title row opens the profile lookup.

import { createListPaneType, renderPaneActions } from '@catalog-shell';
import type { ListPaneConfig, PaneActionDescriptor, PaneTypeRenderContext } from '@catalog-shell';
import { DataSource } from '@catalog-shell/components';
import type { ColumnDef, FetchResult } from '@catalog-shell/components';
import * as trustApi from '../api/trust';
import type { TrustExpressionSummary } from '../api/types';
import { PANE_IDS } from '../shell-config';

const SCORE_LABELS: Record<number, string> = {
    1: 'Distrust',
    2: 'Low',
    3: 'Neutral',
    4: 'Trust',
    5: 'High Trust',
};

const SCORE_COLORS: Record<number, string> = {
    1: 'rgba(220,50,50,0.3)',
    2: 'rgba(220,150,50,0.3)',
    3: 'rgba(100,149,237,0.3)',
    4: 'rgba(80,200,80,0.3)',
    5: 'rgba(50,220,50,0.3)',
};

const columns: ColumnDef<TrustExpressionSummary>[] = [
    {
        key: 'targetCid',
        header: 'Target',
        width: 140,
        getValue: (item) => item.targetCid,
        type: { kind: 'text' },
    },
    {
        key: 'score',
        header: 'Rating',
        width: 100,
        sortable: true,
        getValue: (item) => item.score,
        type: {
            kind: 'badge',
            backgroundColor: (v) => SCORE_COLORS[v as number] ?? '#555',
            converter: (v) => SCORE_LABELS[v as number] ?? String(v),
        },
    },
    {
        key: 'subgraphVisible',
        header: 'Visible',
        width: 80,
        getValue: (item) => item.subgraphVisible,
        type: {
            kind: 'text',
            converter: (v) => v ? 'Yes' : 'No',
            textColor: (v) => v ? '#6c6' : '#888',
        },
    },
    {
        key: 'creationTime',
        header: 'Created',
        width: 100,
        sortable: true,
        getValue: (item) => item.creationTime,
        type: {
            kind: 'text',
            converter: (v) => {
                try { return new Date(v as string).toLocaleDateString(); }
                catch { return String(v); }
            },
        },
        responsiveHide: { minContainerWidth: 600, priority: 1 },
    },
];

function createDataSource(): DataSource<TrustExpressionSummary> {
    return new DataSource<TrustExpressionSummary>({
        fetch: async (params): Promise<FetchResult<TrustExpressionSummary>> => {
            const expressions = await trustApi.getGivenTrust();
            const sorted = [...expressions];
            if (params.sortBy) {
                const key = params.sortBy;
                const dir = params.sortDirection === 'desc' ? -1 : 1;
                sorted.sort((a, b) => {
                    const va = (a as unknown as Record<string, unknown>)[key] ?? '';
                    const vb = (b as unknown as Record<string, unknown>)[key] ?? '';
                    return va < vb ? -dir : va > vb ? dir : 0;
                });
            }
            const sliced = sorted.slice(params.offset, params.offset + params.limit);
            return { items: sliced, totalCount: expressions.length };
        },
        keyFn: (item) => item.targetCid,
        pageSize: 100,
    });
}

/**
 * The list's own verb, for its title row (shell USAGE.md "Where a button
 * belongs"). A rating is always about somebody, and choosing that somebody is
 * what the public profile pane's Look Up form is for — opened with no identity
 * in its context, it asks for one, and the profile it then shows carries the
 * existing Rate verb.
 */
export function trustGivenActions(ctx: PaneTypeRenderContext): PaneActionDescriptor[] {
    return [{
        key: 'rate-identity',
        label: 'Rate an Identity…',
        variant: 'primary',
        execute: () => {
            ctx.surface?.openPane({
                typeId: PANE_IDS.publicProfile,
                title: 'Look Up Identity',
                context: {},
            }, { originPaneId: ctx.instance.instanceId });
        },
    }];
}

const config: ListPaneConfig<TrustExpressionSummary> = {
    typeId: PANE_IDS.trustGiven,
    defaultTitle: 'Trust Given',
    createDataSource: () => createDataSource(),
    columns,
    keyFn: (item) => item.targetCid,
    initialMode: 'table',
    enabledModes: ['table'],
    selectionMode: 'none',
    onCreated: ({ dataSource }, ctx) => {
        ctx.disposables.add(ctx.shell.on('data-changed', (data) => {
            if ((data as { type?: string })?.type === 'trust') {
                dataSource.refresh();
            }
        }));
    },
    onItemSelect: (item, ctx) => {
        ctx.surface?.openPane({
            typeId: PANE_IDS.trustDetail,
            title: `Trust: ${item.targetCid}`,
            context: { trustExpression: item },
        }, { originPaneId: ctx.instance.instanceId });
    },
    tabBarActions: (ctx) => renderPaneActions(trustGivenActions(ctx)),
    ariaLabel: 'Trust expressions given',
};

export function createTrustGivenPane() {
    return createListPaneType(config);
}
